// src/components/common/DocStatusBadge.jsx
import React, { useMemo } from "react";

/* ===================== Icons ===================== */
const CheckIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ClockIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const EditIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

/* ===================== Helpers ===================== */
const cls = (...a) => a.filter(Boolean).join(" ");

const normalizeStatus = (s) => {
  const v = String(s || "").trim();
  if (!v) return "NO_RESPONSE";

  const up = v.toUpperCase();

  // accept canonical values
  if (["NO_RESPONSE", "APPROVED", "REJECTED", "NEEDS_REVISION"].includes(up)) return up;

  // accept legacy/aliases
  if (["PENDING", "WAITING", "NO-RESPONSE", "NO RESPONSE"].includes(up)) return "NO_RESPONSE";
  if (["APPROVE", "APPROVED ", "OK", "YES"].includes(up)) return "APPROVED";
  if (["REJECT", "DECLINED", "DENIED", "NO"].includes(up)) return "REJECTED";
  if (["REVISION", "NEED_REVISION", "NEEDS_FIX", "FIX", "EDIT"].includes(up)) return "NEEDS_REVISION";

  return "NO_RESPONSE";
};

const getView = (status) => {
  switch (status) {
    case "APPROVED":
      return {
        label: "מאושר",
        pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
        icon: <CheckIcon />,
      };

    case "REJECTED":
      return {
        label: "נדחה",
        pill: "bg-red-50 text-red-700 border-red-200",
        icon: <XIcon />,
      };

    case "NEEDS_REVISION":
      return {
        label: "דורש תיקון",
        pill: "bg-purple-50 text-purple-700 border-purple-200",
        icon: <EditIcon />,
      };

    case "NO_RESPONSE":
    default:
      return {
        label: "ממתין לאישור",
        pill: "bg-amber-50 text-amber-700 border-amber-200",
        icon: <ClockIcon />,
      };
  }
};

/* ===================== Component ===================== */
export const DocStatusBadge = ({ status, className = "" }) => {
  const norm = useMemo(() => normalizeStatus(status), [status]);
  const view = useMemo(() => getView(norm), [norm]);

  return (
    <span
      className={cls(
        "inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold border",
        view.pill,
        className
      )}
      title={view.label}
    >
      <span className="inline-flex items-center justify-center">{view.icon}</span>
      <span>{view.label}</span>
    </span>
  );
};

export default DocStatusBadge;
